import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api.js";
import { useAuth } from "./auth.js";
import type { OrgListItem } from "@identity/types";

const LS_KEY = "identity_org_id";

interface OrgContextValue {
  orgs: OrgListItem[];
  currentOrg: OrgListItem | null;
  setCurrentOrgId: (id: string) => void;
  loadingOrgs: boolean;
  refetchOrgs: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue>({
  orgs: [],
  currentOrg: null,
  setCurrentOrgId: () => {},
  loadingOrgs: true,
  refetchOrgs: async () => {},
});

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<OrgListItem[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(
    () => localStorage.getItem(LS_KEY),
  );
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  const refetchOrgs = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setLoadingOrgs(false);
      return;
    }
    try {
      const data = await api.get<{ orgs: OrgListItem[] }>("/orgs/mine");
      const list = data.orgs ?? [];
      setOrgs(list);

      // Restore persisted selection or default to first org
      const stored = localStorage.getItem(LS_KEY);
      if (stored && list.some((o) => o.id === stored)) {
        setCurrentOrgIdState(stored);
      } else if (list.length > 0) {
        setCurrentOrgIdState(list[0]!.id);
        localStorage.setItem(LS_KEY, list[0]!.id);
      } else {
        setCurrentOrgIdState(null);
        localStorage.removeItem(LS_KEY);
      }
    } catch {
      setOrgs([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [user]);

  useEffect(() => {
    setLoadingOrgs(true);
    void refetchOrgs();
  }, [refetchOrgs]);

  const setCurrentOrgId = useCallback((id: string) => {
    setCurrentOrgIdState(id);
    localStorage.setItem(LS_KEY, id);
  }, []);

  const currentOrg = orgs.find((o) => o.id === currentOrgId) ?? orgs[0] ?? null;

  return (
    <OrgContext.Provider value={{ orgs, currentOrg, setCurrentOrgId, loadingOrgs, refetchOrgs }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}
